import React from 'react';
import {useParams} from 'react-router-dom';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {Trash2} from "lucide";
import {LucideIcon} from "~/components/LucideIcon";
import {DetailsView} from '~/components/DetailsView';
import {useConfirm} from '~/components/Dialogs';
import {onSubmitError} from '~/components/Form';
import {useModals} from '~/components/Modal';
import {BreadcrumbsResourcePage} from '~/components/ResourcePage';
import {TableSort} from '~/components/Table';
import {Title} from '~/components/Title';
import {Intent} from '~/lib/intent';
import {ResourceLoader, useResource} from '~/lib/loader';
import {sortByString} from '~/lib/sort';
import {usersLinks} from '~/modules/Users';
import {createUserPermission, deleteUserPermission, fetchUser, updateUser} from '~/modules/Users/api';
import {UserPermissionPresets} from '~/modules/Users/components/UserPermissionPresets';
import {UserForm} from '~/modules/Users/forms/UserForm';
import {UserPermissionForm} from '~/modules/Users/forms/UserPermissionForm';
import {getUserRoleLabel} from '~/modules/Users/models/User';
import {
    defaultUserPermission,
    IUserPermission,
    MediationPartnerType, mediationPartnerTypes,
    PermissionResourceType
} from '~/modules/Users/models/UserPermission';

interface IUserViewParams {
    userId: string;
}

interface UserViewPermission {
    resourceId: string;
    resourceType: string;
    original: IUserPermission;
}

function userPermissionsToView(userPermissions?: IUserPermission[]): UserViewPermission[] {
    if (!userPermissions) {
        return [];
    } else {
        return userPermissions.map(permission => {
            const transformedPermission: UserViewPermission = {
                resourceId: permission.resourceType === PermissionResourceType.MEDIATIONPARTNER ?
                    mediationPartnerTypes.find(key => MediationPartnerType[key] === permission.resourceId) :
                    permission.resourceId.toString(),
                resourceType: permission.resourceType,
                original: permission
            };

            return transformedPermission;
        });
    }
}

export const UserView = () => {
    const params = useParams<IUserViewParams>();
    const result = useResource(() => fetchUser(params.userId));
    const modals = useModals();
    const confirm = useConfirm();
    const editUser = modals.create(UserForm, {
        onSubmit: (user, formikActions, close) => {
            updateUser(user).then(result.setResource).then(close).catch(onSubmitError(formikActions));
        }
    });

    const createPermission = modals.create(UserPermissionForm, {
        title: 'Create permission',
        onSubmit: (permission, formikActions, close) => {
            createUserPermission({
                ...permission,
                resourceId: parseInt(permission.resourceId as any, 10)
            }).then(result.fetch).then(close).catch(onSubmitError(formikActions));
        }
    });

    return <ResourceLoader result={result} message='Loading user details'>
        {({resource: user, isPending}) => <Breadcrumb breadcrumb={{
            to: usersLinks.user(user.id),
            title: user.name
        }}><BreadcrumbsResourcePage title='User'
                toolbar={<Button responsive
                    key='edit'
                    disabled={isPending}
                    leftIcon={<i className='fa fa-edit'/>}
                    onClick={() => editUser.show({user})}>
                                            Edit
                </Button>}>
                <DetailsView fields={[
                    {label: 'Name', value: user.name},
                    {label: 'Email', value: user.email},
                    {label: 'Company', value: user.company},
                    {label: 'Role', value: getUserRoleLabel(user.role)}
                ]}/>
                <div className="layout-content-box">
                    <Title toolbar={<Button intent={Intent.PRIMARY}
                        responsive
                        onClick={() => createPermission.show({
                            permission: {
                                ...defaultUserPermission,
                                userId: user.id
                            }
                        })}
                        leftIcon={<i className='fa fa-plus'/>}
                    >Create</Button>}
                    >Permissions</Title>
                    <TableSort
                    header
                    columns={[
                        {
                            field: 'resourceId',
                            sort: sortByString,
                            header: 'Resource ID'
                        }, {
                            field: 'resourceType',
                            header: 'Resource Type',
                            sort: sortByString
                        }, {
                            cell: permission => <Button small
                                intent={Intent.DANGER}
                                leftIcon={<i className='fa fa-trash'/>}
                                responsive
                                onClick={() => confirm('Delete permission?', {
                                    onOk: () => deleteUserPermission(permission.original).then(result.fetch),
                                    icon: <LucideIcon icon={Trash2} size={20} />,
                                    intent: Intent.DANGER
                                })}
                            >Delete</Button>,
                            small: true
                        }
                    ]}
                    records={userPermissionsToView(user.permissions)}/>
                </div>
                {(user.permissions ?? []).some(p =>
                    p.resourceType === PermissionResourceType.SELLER || p.resourceType === PermissionResourceType.AGENCY
                ) && <div className="layout-content-box">
                    <Title>Presets</Title>
                    <UserPermissionPresets permissions={user.permissions}/>
                </div>}
            </BreadcrumbsResourcePage></Breadcrumb>}
    </ResourceLoader>;
};
